import { AccountInterface, PaymentModeId } from '../interfaces'
import { ServiceDTO } from '../interfaces/Services/Services'

/**
 * Servicios autorizados del contrato para la modalidad de pago actual.
 *
 * Extraído de ShipmentBulkUploadReview (donde vivía inline) porque ahora hay TRES
 * consumidores que deben ver exactamente la misma lista: el Review (dropdown por guía),
 * la plantilla Excel (columna "Servicio" + su dropdown) y el parser del import. Si la
 * plantilla ofreciera un servicio que el Review no acepta, el operador elegiría en Excel
 * algo que el Review le reemplazaría en silencio.
 */

/**
 * Autorizaciones (`listAuthorizingAccountClient`) aplicables a la modalidad actual.
 * Crédito: las del accountSite del usuario con paymentModeID = CRÉDITO.
 * Resto de modalidades: las que no tienen accountSite, con esa modalidad.
 */
export function authorizingAccountsForMode(
  client: any,
  paymentMode: string | number | undefined
): AccountInterface[] {
  const listAuthorizing = (client?.listAuthorizingAccountClient ??
    []) as AccountInterface[]
  const accountSiteID = client?.accountSiteID
  if (paymentMode?.toString() === PaymentModeId.CREDIT.toString()) {
    return listAuthorizing.filter(
      (x) =>
        x.accountSiteID?.toString() === accountSiteID?.toString() &&
        x.paymentModeID?.toString() ===
          parseInt(PaymentModeId.CREDIT)?.toString()
    )
  }
  return listAuthorizing.filter(
    (x) =>
      x.accountSiteID == null &&
      x.paymentModeID === parseInt(paymentMode as string)
  )
}

/** Servicios permitidos (hook) ∩ autorizados por el contrato para la modalidad. */
export function authorizedServiceOptions(
  allowedServices: ServiceDTO[],
  client: any,
  paymentMode: string | number | undefined
): ServiceDTO[] {
  const authorized = authorizingAccountsForMode(client, paymentMode).map(
    (a) => a.serviceID
  )
  return (allowedServices ?? []).filter((s) =>
    authorized.includes(s.serviceID)
  )
}

/**
 * serviceIDs que exigen valor declarado (isValueDeclare) para la modalidad/sitio actual.
 * Mismo filtro que `authorizedServiceOptions` para no arrastrar autorizaciones de
 * otras modalidades.
 */
export function valueDeclareServiceIdsFor(
  client: any,
  paymentMode: string | number | undefined
): Set<number> {
  const ids = new Set<number>()
  authorizingAccountsForMode(client, paymentMode).forEach((a) => {
    if (a.isValueDeclare && a.serviceID != null) ids.add(Number(a.serviceID))
  })
  return ids
}

/**
 * Etiqueta de un servicio en la plantilla Excel. Es EXACTAMENTE el mismo texto que el
 * dropdown del Review (`serviceShortName`), para que el operador vea lo mismo en los dos
 * lados y el round-trip Excel→Review no sorprenda.
 */
export function serviceLabel(s: {
  serviceShortName?: string
  serviceDescription?: string
  serviceID?: number
}): string {
  return (
    s.serviceShortName?.trim() ||
    s.serviceDescription?.trim() ||
    String(s.serviceID ?? '')
  )
}

/**
 * Resuelve la celda "Servicio" del Excel contra los servicios autorizados.
 *
 * Devuelve `undefined` cuando la celda viene vacía (el Review auto-asigna el primero,
 * igual que antes de existir la columna) y `null` cuando trae texto que NO resuelve
 * (el caller lo marca como aviso en la fila en vez de sustituir en silencio).
 * Tolera nombre corto, descripción, el ID numérico y el formato "ID - nombre".
 */
export function resolveServiceIdFromCell(
  cell: string,
  serviceOptions: ServiceDTO[]
): number | undefined | null {
  const raw = (cell || '').trim()
  if (!raw) return undefined
  const norm = raw.toLowerCase()
  const match = serviceOptions.find(
    (s) =>
      serviceLabel(s).toLowerCase() === norm ||
      (s.serviceShortName || '').trim().toLowerCase() === norm ||
      (s.serviceDescription || '').trim().toLowerCase() === norm ||
      String(s.serviceID) === raw ||
      `${s.serviceID} - ${serviceLabel(s)}`.toLowerCase() === norm
  )
  return match ? match.serviceID : null
}
